import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-1b5e9f4f9d/icons";
import { useScreenshot, createFileName } from "use-react-screenshot";
import imageKeywords from "../imageIndex-sketch.json";
import { formatCurrency, calculateEffortDays, calculateScheduleWeeks } from "../view-util.js";
import { ProjectRepository } from "../ProjectRepository.js";
import canvasTemplate from "../canvas-template.json";
import EstimateTable from "./EstimateTable";
import Flow from "./Flow";
import Card from "./Card";
import StrategyTab from "./StrategyTab";
import EditableParagraph from "./EditableParagraph";
import FlowBotDropdown from "./FlowBotDropdown";
import TagSelector from "./TagSelector";
import ShareTab from "./ShareTab";


const UserFlowView = ({
  settings: settings,
  getPDF: getPDF,
  title: title,
  description: description,
  flows: flows,
  updateImagePickerStory: updateImagePickerStory,
  imageKeywords: imageKeywords,
  showPoints: showPoints,
  showPrice: showPrice,
  totalPrice: totalPrice,
  totalPoints: totalPoints,
  project: project,
  hideBigPicture: hideBigPicture = true,
  isPreviewMode: isPreviewMode = false,
  onUpdateProject: onUpdateProject,
  onTabSelected: onTabSelected,
  onStrategyFieldSelected: onStrategyFieldSelected,
  initialSelectedTab: initialSelectedTab = "flows-tab",
  allTags: allTags = [], // All available tags from the document
  previewConfig: previewConfig = null, // In shared previews, gates which tabs are shown
}) => {
  // In a shared preview only the ticked tabs appear; in the editor (no config) all show.
  const showTab = (key) => !previewConfig || previewConfig[key];
  // useEffect(()=>{
  //   console.log("Rendering UserFlowView")
  // },[ref])

  const ref = useRef(null);
  const [image, takeScreenshot] = useScreenshot();
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
  const [selectedTags, setSelectedTags] = useState([]);
  const [visibleFlows, setVisibleFlows] = useState(flows);
  const [filteredTotalPoints, setFilteredTotalPoints] = useState(totalPoints);
  const [filteredTotalPrice, setFilteredTotalPrice] = useState(totalPrice);
  const [storyDescriptions, setStoryDescriptions] = useState({});
  const [sheetsCopied, setSheetsCopied] = useState(false);
  const netlifyIdentity = require("netlify-identity-widget");

  const getImage = () => {
    document.getElementById("pdf-button").classList.toggle("is-hidden");
    document.getElementById("copy-button").classList.toggle("is-hidden");
    document.getElementById("credits-text").classList.toggle("is-hidden");
    takeScreenshot(ref.current).then((s) => {
      fetch(s).then((d) => {
        console.log(s);
        d.blob().then((b) => {
          document.getElementById("pdf-button").classList.toggle("is-hidden");
          document.getElementById("copy-button").classList.toggle("is-hidden");
          document.getElementById("credits-text").classList.toggle("is-hidden");
          navigator.clipboard.write([new ClipboardItem({ [b.type]: b })]);
        });
      });
    });
  };



  const toggleTabs = (selectedName) => {
    setSelectedTab(selectedName);
    // ["flows-tab", "strategy-tab", "estimate-tab"].map((n) => {
    //   const tab = document.getElementById(`${n}-item`);
    //   const content = document.getElementById(`${n}-content`);
    //   if (n === selectedName && content && tab) {
    //     tab.classList.add("is-active");
    //     content.classList.remove("is-hidden");
    //   } else if (content && tab) {
    //     tab.classList.remove("is-active");
    //     content.classList.add("is-hidden");
    //   }
    //   if(onTabSelected) onTabSelected(selectedName)
    // });
    if (onTabSelected) onTabSelected(selectedName);
    // document.getElementById("flows-tab").classList.toggle("is-hidden");
    // document.getElementById("strategy-tab").classList.toggle("is-hidden");
    // document.getElementById("flows-tab-item").classList.toggle("is-active");
    // document.getElementById("strategy-tab-item").classList.toggle("is-active");
  };

  const toggleTag = (tag) => {
    setSelectedTags(prevTags => {
      if (prevTags.includes(tag)) {
        return prevTags.filter(t => t !== tag);
      } else {
        return [...prevTags, tag];
      }
    });
  };
  
  // Toggle between short and full description for a story
  const toggleDescription = (story) => {
    setStoryDescriptions(prev => ({
      ...prev,
      [story.lineNo]: {
        ...prev[story.lineNo],
        showFullDescription: !((prev[story.lineNo] || {}).showFullDescription || false)
      }
    }));
  };

  useEffect(() => {
    // Process all flows, regardless of tag selection
    const processedFlows = flows.map(flow => {
      // When no tags are selected, everything is fully visible
      if (selectedTags.length === 0) {
        return {
          ...flow,
          visible: true, // Full opacity
          stories: flow.stories.map(story => ({
            ...story,
            visible: true // Full opacity
          })),
          filteredPoints: flow.stories.reduce((sum, story) => sum + (story.size || 0), 0)
        };
      }
      
      // When tags are selected, check if flow has any of the selected tags
      const flowHasTag = flow.tags && flow.tags.some(tag => selectedTags.includes(tag));
      
      // Process all stories, marking which ones have the selected tags
      const processedStories = flow.stories.map(story => {
        const storyHasTag = story.tags && story.tags.some(tag => selectedTags.includes(tag));
        
        return {
          ...story,
          visible: storyHasTag || flowHasTag // Controls opacity only
        };
      });
      
      // Calculate points for stories with the selected tags only
      const taggedPoints = processedStories
        .filter(story => story.visible)
        .reduce((sum, story) => sum + (story.size || 0), 0);
      
      // A flow should only be faded if ALL of its stories don't match the selected tag
      // If any story matches the tag, the flow should be fully visible
      const anyStoryHasTag = processedStories.some(story => story.visible);
      
      return {
        ...flow,
        stories: processedStories,
        visible: flowHasTag || anyStoryHasTag, // Flow is visible if it has the tag OR any of its stories have the tag
        filteredPoints: taggedPoints
      };
    });
    
    // Calculate totals based on tag selection
    if (selectedTags.length === 0) {
      // If no tags selected, use original totals
      setVisibleFlows(processedFlows);
      setFilteredTotalPoints(totalPoints);
      setFilteredTotalPrice(totalPrice);
    } else {
      // If tags selected, calculate totals for tagged items only
      const newTotalPoints = processedFlows.reduce((sum, flow) => 
        sum + flow.filteredPoints, 0);
      const newTotalPrice = newTotalPoints * settings.pointPrice;
      
      setVisibleFlows(processedFlows);
      setFilteredTotalPoints(newTotalPoints);
      setFilteredTotalPrice(newTotalPrice);
    }
  }, [selectedTags, flows, totalPoints, totalPrice, settings.pointPrice]);





  const updateStrategy = (field, value) => {
    console.log(`Updating ${field} to ${value}`);
    const tmp = structuredClone(project);
    if (!tmp.strategy) tmp.strategy = {};
    tmp.strategy[field] = value;
    if (onUpdateProject) onUpdateProject(tmp);
  };



  const downloadCSV = (type) => {
    const csvData = flows.map(flow =>
      flow.stories.map(story => ({
        title: story.title,
        description: story.text.replace(/<[^>]*>/g, ''),
        points: story.size,
        epic: flow.name
      }))
    ).flat();

    let csvContent = '';

    switch (type) {
      case 'jira':
        csvContent = 'Summary,Description,Story Points,Epic Link\n' +
          csvData.map(row =>
            `"${row.title}","${row.description}",${row.points},"${row.epic}"`
          ).join('\n');
        break;
      case 'linear':
        csvContent = 'Title,Description,Estimate,Team\n' +
          csvData.map(row =>
            `"${row.title}","${row.description}",${row.points},"${row.epic}"`
          ).join('\n');
        break;
      // Add other formats as needed
      default:
        csvContent = 'Title,Description,Points,Epic\n' +
          csvData.map(row =>
            `"${row.title}","${row.description}",${row.points},"${row.epic}"`
          ).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${project.name}-${type}.csv`;
    link.click();
  };

  // Build spreadsheet rows for Google Sheets: one row per story with live
  // SPARKLINE and Price formulas, plus a Yes/No column per tag. The point
  // price sits in its own cell on row 1 (after the tag columns) so each Price
  // formula can reference it and it stays editable in the sheet.
  // Columns: A Section, B Short Name, C Story Text, D Point Estimate,
  //          E Relative Size (sparkline), F Price, then one column per tag,
  //          then "Point Price" label + value.
  const buildSheetsRows = () => {
    // 1 -> A, 26 -> Z, 27 -> AA ...
    const colLetter = (n) => {
      let s = "";
      while (n > 0) {
        s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    };

    const stories = flows.flatMap((flow) =>
      flow.stories.map((story) => ({
        section: flow.name,
        title: story.title,
        text: (story.text || "").replace(/<[^>]*>/g, ""),
        points: story.size || 0,
        // A story carries a tag if it has it directly or inherits it from its section.
        tags: [...(story.tags || []), ...(flow.tags || [])],
      }))
    );

    // 6 fixed columns + one per tag, then the point-price value cell.
    const priceCell = `$${colLetter(6 + allTags.length + 2)}$1`;
    // Point-estimate data range (rows 2..lastDataRow, excluding the totals row)
    // so every sparkline bar shares the column's max and stays comparable.
    const lastDataRow = stories.length + 1;
    const pointsRange = `$D$2:$D$${lastDataRow}`;

    const header = [
      "Section", "Short Name", "Story Text", "Point Estimate",
      "Relative Size", "Price", ...allTags, "Point Price", settings.pointPrice,
    ];

    const rows = stories.map((s, i) => {
      const rowNo = i + 2; // row 1 is the header
      return [
        s.section,
        s.title,
        s.text,
        s.points,
        `=SPARKLINE(D${rowNo}, {"charttype","bar";"max",MAX(${pointsRange})})`,
        `=D${rowNo}*${priceCell}`,
        ...allTags.map((tag) => (s.tags.includes(tag) ? "Yes" : "No")),
      ];
    });

    if (rows.length === 0) return [header];

    // Totals row: sum points/price and count "Yes" for each tag.
    const lastRow = rows.length + 1; // last story row (header is row 1)
    const totals = [
      "Total", "", "",
      `=SUM(D2:D${lastRow})`,
      "",
      `=SUM(F2:F${lastRow})`,
      ...allTags.map((tag, j) => {
        const col = colLetter(7 + j); // tag columns start at G
        return `=COUNTIF(${col}2:${col}${lastRow}, "Yes")`;
      }),
    ];

    return [header, ...rows, totals];
  };

  // Copy the estimate as plain tab-delimited text so it pastes straight into a
  // Google Sheet (Cmd/Ctrl+V). Sheets evaluates the SPARKLINE/Price/total
  // formulas on paste exactly as if typed. (A rich text/html payload could
  // carry currency formatting too, but Sheets treats its formula cells as
  // literal text, so we keep the reliable plain-text path — currency is a
  // one-click column format in the sheet.)
  const copyForSheets = () => {
    const clean = (v) => String(v).replace(/[\t\r\n]+/g, " ");
    const tsv = buildSheetsRows()
      .map((row) => row.map(clean).join("\t"))
      .join("\n");
    navigator.clipboard.writeText(tsv).then(() => {
      setSheetsCopied(true);
      setTimeout(() => setSheetsCopied(false), 2000);
    });
  };

  // Same layout as a CSV file (Google Sheets evaluates formulas on Import).
  const downloadSheetsCSV = () => {
    const csvCell = (v) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = buildSheetsRows()
      .map((row) => row.map(csvCell).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${project.name}-google-sheets.csv`;
    link.click();
  };

  return (
    <div>
      <div className="tabs is-boxed doc-tabs no-print">
        <ul style={{ border: "0px" }}>
          {!hideBigPicture && showTab("bigPicture") ? (
            <li
              id="strategy-tab-item"
              className={`${selectedTab == "strategy-tab" ? "is-active" : ""}`}
              data-tab="3"
            >
              <a
                className="has-text-dark"
                onClick={() => toggleTabs("strategy-tab")}
              >
                Big Picture
              </a>
            </li>
          ) : null}
          {showTab("flows") ? (
            <li
              id="flows-tab-item"
              className={`${selectedTab == "flows-tab" ? "is-active" : ""}`}
              data-tab="1"
            >
              <a
                className="has-text-dark"
                onClick={() => toggleTabs("flows-tab")}
              >
                User Flows
              </a>
            </li>
          ) : null}
          {showTab("estimate") ? (
            <li
              id="estimate-tab-item"
              className={`${selectedTab == "estimate-tab" ? "is-active" : ""}`}
              data-tab="2"
            >
              <a
                className="has-text-dark"
                onClick={() => toggleTabs("estimate-tab")}
              >
                Estimate
              </a>
            </li>
          ) : null}
          {!isPreviewMode ? (
            <li
              id="share-tab-item"
              className={`${selectedTab == "share-tab" ? "is-active" : ""}`}
              data-tab="4"
            >
              <a
                className="has-text-dark"
                onClick={() => toggleTabs("share-tab")}
              >
                Share
              </a>
            </li>
          ) : null}
        </ul>
      </div>
      {/* <FlowBotDropdown/> */}

      <div
        id="flows-tab-content"
        className={`handwriting has-background-white box pb-6 ${selectedTab == "flows-tab" ? "" : "is-hidden"
          }`}
        style={{ "border-radius": "0px 10px 10px 10px" }}
        data-content="1"
      >
        <div ref={ref}>
          <section className="section content">
            <div
              id="credits-text"
              className="has-text-grey is-size-7 is-pulled-left is-hidden"
            >
              Created with appflows.ai
            </div>
            <button
              id="copy-button"
              data-title="Share"
              data-intro="Share by copying an image to clipboard."
              data-step="4"
              className="button is-text is-pulled-right no-print is-size-7"
              onClick={getImage}
              style={{ textDecoration: "none" }}
            >
              {" "}
              <FontAwesomeIcon
                className="pl-1"
                icon={byPrefixAndName.fal["camera"]}
              />
              &nbsp;Copy
            </button>
            <button
              id="pdf-button"
              data-title="PDF Download"
              data-intro="Or download a PDF. You can also Ctrl-P to print."
              data-step="5"
              className="button is-text is-pulled-right no-print is-size-7"
              onClick={getPDF}
              style={{ textDecoration: "none" }}
            >
              {" "}
              <FontAwesomeIcon
                className="pl-1"
                icon={byPrefixAndName.fal["file-pdf"]}
              />
              &nbsp;PDF
            </button>
            <div className="mb-2 pt-4 no-print">
              <TagSelector 
                allTags={allTags}
                selectedTags={selectedTags}
                toggleTag={toggleTag}
              />
            </div>
            <h2 className="subtitle is-size-5 mb-0 has-text-dark has-text-weight-normal pb-0">
              User Flows
            </h2>
            <h1 className="title is-1 mt-2 mb-3">{title}</h1>
            {!isPreviewMode ? (
              <div className="box has-background-white-ter is-shadowless no-print">
                <FontAwesomeIcon
                  className="pr-3"
                  icon={byPrefixAndName.fas["circle-2"]}
                />
                Write the user stories to scope out the app, using the editor to
                the right.
              </div>
            ) : null}
            <p className="has-text-grey is-hidden ">
              Last updated:{" "}
              {project?.lastUpdated
                ? `${new Date(
                  project.lastUpdated
                ).toLocaleDateString()} ${new Date(
                  project.lastUpdated
                ).toLocaleTimeString()}`
                : "Never"}
            </p>
            <p className="has-text-grey pt-3">
              {settings.showPoints ? `Total size: ${filteredTotalPoints} points. ` : ""}
              {settings.showPrice
                ? `Estimated cost: ${formatCurrency(filteredTotalPrice, settings.currency)}. `
                : ""}
              {settings.showPrice && settings.dayRate
                ? `Allows ${calculateEffortDays(filteredTotalPrice, settings.dayRate)} days effort`
                : ""}
              {settings.showPrice && settings.dayRate && settings.teamSize
                ? ` over ${calculateScheduleWeeks(calculateEffortDays(filteredTotalPrice, settings.dayRate), settings.teamSize)} weeks.`
                : settings.showPrice && settings.dayRate ? "." : ""}
            </p>
            <div
              className="has-text-weight-semibold"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </section>
          <div className="content">
            <div className="flows">
              {visibleFlows.map((flow) => (
                <Flow
                  key={flow.lineNo}
                  flow={flow}
                  settings={settings}
                  updateImagePickerStory={updateImagePickerStory}
                  visible={flow.visible}
                  toggleDescription={toggleDescription}
                  storyDescriptions={storyDescriptions}
                />
              ))}
            </div>
          </div>
          {flows.length > 0 && settings.showTable ? (
            <EstimateTable flows={visibleFlows} settings={settings} selectedTags={['PoC']} />
          ) : null}
          <div
            className={`column mt-6 is-12 box has-background-white-ter is-shadowless no-print`}
          >
            Not sure about your features? We offer professional app design and
            market-validation services,{" "}
            <a href="https://pocketworks.co.uk/services" target="_blank">
              check em' out
            </a>
            .
          </div>
        </div>
      </div>
      <div
        id="estimate-tab-content"
        className={`box handwriting pb-6 ${selectedTab == "estimate-tab" ? "" : "is-hidden"
          }`}
        data-content="2"
        style={{ "border-radius": "0px 10px 10px 10px" }}
      >
        <section className="content section">
          <div className="pt-4"></div>
          <div
            id="credits-text"
            className="has-text-grey is-size-7 is-pulled-left is-hidden"
          >
            Created with appflows.ai
          </div>          
          <button
              id="pdf-button"
              data-title="PDF Download"
              data-intro="Or download a PDF. You can also Ctrl-P to print."
              data-step="5"
              className="button is-text is-pulled-right no-print is-size-7"
              onClick={getPDF}
              style={{ textDecoration: "none" }}
            >
              {" "}
              <FontAwesomeIcon
                className="pl-1"
                icon={byPrefixAndName.fal["file-pdf"]}
              />
              &nbsp;PDF
            </button>
          <div className="dropdown is-hoverable is-right is-pulled-right no-print">
            <div className="dropdown-trigger">
              <button className="button is-text is-size-7" style={{ textDecoration: "none" }}>
                <FontAwesomeIcon
                  className="pl-1"
                  icon={byPrefixAndName.fal["file-csv"]}
                />
                &nbsp;Export
                <FontAwesomeIcon
                  className="pl-1"
                  icon={byPrefixAndName.fal["chevron-down"]}
                />
              </button>
            </div>
            <div className="dropdown-menu">
              <div className="dropdown-content">
                <a className="dropdown-item" onClick={() => downloadCSV('jira')}>
                  <FontAwesomeIcon
                    className="pr-2"
                    icon={byPrefixAndName.fab["jira"]}
                  />
                  Export for Jira
                </a>
                <a className="dropdown-item" onClick={() => downloadCSV('linear')}>
                  <span className="icon pr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="200" height="200" viewBox="0 0 100 100"><path fill="#222326" d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z" /></svg>
                  </span>
                  Export for Linear
                </a>
                <a className="dropdown-item" onClick={() => downloadCSV('github')}>
                  <FontAwesomeIcon
                    className="pr-2"
                    icon={byPrefixAndName.fab["github"]}
                  />
                  Export for Github
                </a>
                <a className="dropdown-item" onClick={() => downloadCSV('asana')}>
                  <FontAwesomeIcon
                    className="pr-2"
                    icon={byPrefixAndName.fal["file-csv"]}
                  />
                  Export for Asana
                </a>
                <hr className="dropdown-divider" />
                <a className="dropdown-item" onClick={copyForSheets}>
                  <FontAwesomeIcon
                    className="pr-2"
                    icon={byPrefixAndName.fab["google"]}
                  />
                  {sheetsCopied ? "Copied — paste into a Sheet!" : "Copy for Google Sheets"}
                </a>
                <a className="dropdown-item" onClick={downloadSheetsCSV}>
                  <FontAwesomeIcon
                    className="pr-2"
                    icon={byPrefixAndName.fal["file-csv"]}
                  />
                  Export for Google Sheets (CSV)
                </a>
              </div>
            </div>
          </div>
          <div className="mb-2 pt-4 no-print">
            <TagSelector 
              allTags={allTags}
              selectedTags={selectedTags}
              toggleTag={toggleTag}
            />
          </div>
          <h2 className="subtitle is-size-5 mb-0 has-text-dark has-text-weight-normal pb-0">
            Estimate
          </h2>
          <h1 className="title is-1 mt-2 mb-3">{project.name}</h1>
          <p className="has-text-grey pt-1 pb-3">
            {settings.showPoints ? `Total size: ${filteredTotalPoints} points. ` : ""}
            {settings.showPrice
              ? `Estimated cost: ${formatCurrency(filteredTotalPrice, settings.currency)}. `
              : ""}
            {settings.showPrice && settings.dayRate
              ? `Allows ${calculateEffortDays(filteredTotalPrice, settings.dayRate)} days effort`
              : ""}
            {settings.showPrice && settings.dayRate && settings.teamSize
              ? ` over ${calculateScheduleWeeks(calculateEffortDays(filteredTotalPrice, settings.dayRate), settings.teamSize)} weeks.`
              : settings.showPrice && settings.dayRate ? "." : ""}
          </p>

          {!isPreviewMode ? (
            <div className="box has-background-white-ter is-shadowless no-print">
              <FontAwesomeIcon
                className="pr-3"
                icon={byPrefixAndName.fas["circle-3"]}
              />
              Add story-points to your stories and use the table to spot the
              risky items. Add estimates by writing point values at the end of
              each story in the editor, such as <code>3pt</code> or{" "}
              <code>8pt</code> etc.
            </div>
          ) : null}

          <EstimateTable flows={visibleFlows} settings={settings} selectedTags={selectedTags}/>
          <div
            className={`column mt-6 is-12 box has-background-white-ter is-shadowless no-print`}
          >
            Need your app estimating? We offer a fast-track professional
            estimate for £499,{" "}
            <a href="https://pocketworks.co.uk/contact" target="_blank">
              get in touch
            </a>
            .
          </div>
        </section>
      </div>

      <StrategyTab 
        selectedTab={selectedTab}
        project={project}
        isPreviewMode={isPreviewMode}
        onStrategyFieldSelected={onStrategyFieldSelected}
        updateStrategy={updateStrategy}
        netlifyIdentity={netlifyIdentity}
      />
      <ShareTab 
        selectedTab={selectedTab}
        project={project}
        netlifyIdentity={netlifyIdentity}
      />
    </div>
  );
};





export default UserFlowView;


